import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import { type ReactNode } from "react";

import { Checkbox } from "@/components/ui/Checkbox";
import { cn } from "@/lib/utils";

import { renderCell } from "./cells";
import { DataGridEmptyState } from "./DataGridEmptyState";
import { DataGridLoading } from "./DataGridLoading";
import { DataGridRowMenu } from "./DataGridRowMenu";
import {
  type TActionDef,
  type TColumnAlign,
  type TColumnDef,
  type TDensity,
  type TSortOrder,
} from "./types";

type TSortState = { columnId: string; order: TSortOrder } | null;

type TDataGridTableProps<TRow> = {
  columns: TColumnDef<TRow>[];
  rows: TRow[];
  getRowId: (row: TRow) => string;
  loading: boolean;
  density: TDensity;
  ariaLabel?: string;
  // sort
  sort: TSortState;
  onToggleSort: (columnId: string) => void;
  // selection
  selectionEnabled: boolean;
  selectedIds: Set<string>;
  onToggleRow: (id: string) => void;
  onToggleAll: () => void;
  allSelected: boolean;
  someSelected: boolean;
  // actions / interactions
  rowActions?: (row: TRow) => TActionDef[];
  onRowClick?: (row: TRow) => void;
  // empty state
  emptyState?: {
    icon?: ReactNode;
    title?: string;
    description?: string;
    action?: TActionDef;
    secondaryAction?: TActionDef;
  };
  filtered?: boolean;
};

function alignClass(align?: TColumnAlign): string {
  if (align === "right") return "text-right";
  if (align === "center") return "text-center";
  return "text-left";
}

export function DataGridTable<TRow>({
  columns,
  rows,
  getRowId,
  loading,
  density,
  ariaLabel,
  sort,
  onToggleSort,
  selectionEnabled,
  selectedIds,
  onToggleRow,
  onToggleAll,
  allSelected,
  someSelected,
  rowActions,
  onRowClick,
  emptyState,
  filtered,
}: TDataGridTableProps<TRow>) {
  const cellPad = density === "compact" ? "px-4 py-2" : "px-4 py-3.5";
  const hasRowActions = Boolean(rowActions);
  const totalColumns = columns.length + (selectionEnabled ? 1 : 0) + (hasRowActions ? 1 : 0);

  return (
    <div className="overflow-x-auto">
      <table
        className="w-full min-w-[640px] border-collapse text-sm"
        aria-label={ariaLabel}
      >
        <thead className="sticky top-0 z-10 bg-muted">
          <tr className="border-b border-border">
            {selectionEnabled && (
              <th
                scope="col"
                className={cn("w-10", cellPad)}
              >
                <Checkbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={onToggleAll}
                  aria-label="Select all rows"
                />
              </th>
            )}
            {columns.map((column) => {
              const isSorted = sort?.columnId === column.id;
              const ariaSort = isSorted ? (sort?.order === "asc" ? "ascending" : "descending") : "none";
              return (
                <th
                  key={column.id}
                  scope="col"
                  aria-sort={column.sortable ? ariaSort : undefined}
                  style={column.width ? { width: column.width } : undefined}
                  className={cn(
                    "text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                    cellPad,
                    alignClass(column.align),
                    column.pinRight && "sticky right-0 z-[1] border-l border-border bg-muted"
                  )}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      onClick={() => onToggleSort(column.id)}
                      className={cn(
                        "inline-flex items-center gap-1 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        column.align === "right" && "flex-row-reverse",
                        column.align === "center" && "justify-center",
                        isSorted && "text-foreground"
                      )}
                    >
                      <span>{column.header}</span>
                      {isSorted ? (
                        sort?.order === "asc" ? (
                          <ChevronUp className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" />
                        )
                      ) : (
                        <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
                      )}
                    </button>
                  ) : (
                    <span>{column.header}</span>
                  )}
                </th>
              );
            })}
            {hasRowActions && (
              <th
                scope="col"
                className={cn("w-12 text-right", cellPad)}
              >
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>

        {loading ? (
          <DataGridLoading
            columnCount={totalColumns}
            density={density}
          />
        ) : rows.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={totalColumns}>
                <DataGridEmptyState
                  {...emptyState}
                  filtered={filtered}
                />
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            {rows.map((row) => {
              const id = getRowId(row);
              const selected = selectedIds.has(id);
              const clickable = Boolean(onRowClick);
              return (
                <tr
                  key={id}
                  className={cn(
                    "group border-b border-border transition-colors hover:bg-muted/60",
                    selected && "bg-primary/5",
                    clickable && "cursor-pointer"
                  )}
                  data-selected={selected || undefined}
                  onClick={clickable ? () => onRowClick?.(row) : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onRowClick?.(row);
                          }
                        }
                      : undefined
                  }
                >
                  {selectionEnabled && (
                    <td
                      className={cellPad}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => onToggleRow(id)}
                        aria-label={`Select row ${id}`}
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      className={cn(
                        cellPad,
                        alignClass(column.align),
                        "text-foreground",
                        // Pinned cells need an opaque background (content scrolls
                        // beneath them); group-hover keeps the row tint in sync.
                        column.pinRight &&
                          "sticky right-0 z-[1] border-l border-border bg-background transition-colors group-hover:bg-muted"
                      )}
                    >
                      {renderCell(column, row)}
                    </td>
                  ))}
                  {hasRowActions && (
                    <td className={cn(cellPad, "text-right")}>
                      <DataGridRowMenu actions={rowActions ? rowActions(row) : []} />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        )}
      </table>
    </div>
  );
}
